import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { catalog } from "./api"
import {
  pilotRequests,
  type EvidenceMatch,
  type PilotFramework,
  type PilotRequest,
} from "./pilotRequestsApi"
import {
  associationKey,
  normalizedPart,
  ReviewAssociations,
  reviewStatusKeys,
} from "./ReviewAssociations"
import { SavedOriginalActions } from "./SavedOriginalActions"

export function RequestMatchingPanel({ request }: { request: PilotRequest }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const canEdit = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  const [document, setDocument] = useState("")
  const [parts, setParts] = useState<string[]>([])
  const [framework, setFramework] = useState("")
  const [autoProgress, setAutoProgress] = useState({
    scanned: 0,
    uploaded: 0,
    reused: 0,
    notAutoMatched: 0,
    matchedPairs: 0,
    failed: [] as string[],
  })
  const docs = useInfiniteQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "match-documents",
      request.companyId,
    ],
    queryFn: ({ pageParam }) => catalog.documents(request.companyId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  })
  const documents = docs.data?.pages.flatMap((p) => p.items) ?? []
  const selected = documents.find((d) => d.id === document)
  const valid = !!selected?.sourceUrl && parts.length > 0 && !!framework
  const refresh = () =>
    client.invalidateQueries({
      queryKey: ["company-index", user?.organization],
    })
  const save = useMutation({
    mutationFn: (matches: EvidenceMatch[]) =>
      pilotRequests.saveMatches(request.id, request.revision, matches),
    onSuccess: () => {
      void refresh()
      setParts([])
    },
  })
  const auto = useMutation({
    mutationFn: async () => {
      let revision = request.revision
      let cursor: string | null = null
      const seen = new Set<string>()
      const totals = {
        scanned: 0,
        uploaded: 0,
        reused: 0,
        notAutoMatched: 0,
        matchedPairs: 0,
        failed: [] as string[],
      }
      setAutoProgress(totals)
      for (let page = 0; page < 1000; page++) {
        const result = await pilotRequests.autoImport(request.id, {
          expectedRevision: revision,
          cursor,
        })
        revision = result.revision
        totals.scanned += result.scanned

        totals.notAutoMatched += result.notAutoMatched
        totals.matchedPairs += result.matchedPairs
        totals.failed.push(...result.failed)
        setAutoProgress({ ...totals })
        if (!result.nextCursor) return totals
        if (seen.has(result.nextCursor))
          throw new Error("Automatic import cursor repeated")
        seen.add(result.nextCursor)
        cursor = result.nextCursor
      }
      throw new Error("Automatic import exceeded 1000 batches")
    },
    onSettled: () => {
      void client.invalidateQueries({
        queryKey: [
          "company-index",
          user?.organization,
          "research-requests",
          request.companyId,
        ],
      })
    },
  })
  const submissions = useInfiniteQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "review-submissions",
      request.id,
    ],
    queryFn: ({ pageParam }) =>
      pilotRequests.submissions(request.id, pageParam),
    initialPageParam: 0,
    getNextPageParam: (page, pages) =>
      page.length === 100 ? pages.length * 100 : undefined,
  })
  const send = useMutation({
    mutationFn: (id: string) =>
      pilotRequests.sendForReview(request.id, request.revision, [id]),
    onSuccess: () => {
      void refresh()
    },
  })
  const excluded = new Set(
    request.matches
      .filter(
        (m) =>
          m.decision === "excluded" && m.matchMethod !== "automatic_exact_pdf",
      )
      .map((m) =>
        JSON.stringify([
          m.sha256,
          normalizedPart(m.partNumber, m.issuerDomain),
          m.framework,
        ]),
      ),
  )
  const supported = request.matches.filter(
    (m) =>
      m.decision === "supported" &&
      m.issuerDomain === request.scope.issuerDomain &&
      !excluded.has(
        JSON.stringify([
          m.sha256,
          normalizedPart(m.partNumber, m.issuerDomain),
          m.framework,
        ]),
      ),
  )
  const groups = [...new Set(supported.map((m) => m.candidateId))].map(
    (id) => ({ id, matches: supported.filter((m) => m.candidateId === id) }),
  )
  // Matching scans all document pages; resolve names and originals for matches
  // beyond the first catalog page without asking the sender to load them manually.
  const missingDocument = supported.some(
    (m) => !documents.some((d) => d.id === m.candidateId),
  )
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError: documentError,
  } = docs
  useEffect(() => {
    if (missingDocument && hasNextPage && !isFetchingNextPage && !documentError)
      // Panels of one company share this query; reuse an in-flight page fetch.
      void fetchNextPage({ cancelRefetch: false })
  }, [
    missingDocument,
    hasNextPage,
    isFetchingNextPage,
    documentError,
    fetchNextPage,
  ])
  const busy = save.isPending || auto.isPending || send.isPending
  const selectClass =
    "mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
  return (
    <div className="space-y-3 mt-4 border-t pt-4">
      <h3 className="font-medium">{t("CI_MATCH_TITLE")}</h3>
      <p className="text-sm text-muted-foreground">{t("CI_MATCH_HINT")}</p>
      {canEdit && (
        <div className="space-y-2 rounded-lg border bg-card p-3">
          <Button disabled={busy} onClick={() => auto.mutate()}>
            {t("CI_AUTO_IMPORT")}
          </Button>
          <p className="text-sm text-muted-foreground">{t("CI_AUTO_HINT")}</p>
          {(auto.isPending || auto.isSuccess) && (
            <p role="status">
              {autoProgress.scanned} {t("CI_AUTO_SCANNED")} ·{" "}
              {autoProgress.matchedPairs} {t("CI_AUTO_MATCHED")} ·{" "}
              {autoProgress.notAutoMatched} {t("CI_AUTO_LEFT_OUT")}
            </p>
          )}
          {!!autoProgress.failed.length && (
            <p role="alert">
              {t("CI_AUTO_FAILED")}: {autoProgress.failed.join(", ")}
            </p>
          )}
          {auto.isError && <p role="alert">{t("CI_AUTO_ERROR")}</p>}
        </div>
      )}
      {submissions.isPending && <p>{t("CI_LOADING")}</p>}
      {submissions.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void submissions.refetch()}>
            {t("CI_RETRY")}
          </Button>
        </p>
      )}
      {!groups.length && <p>{t("CI_MATCH_EMPTY")}</p>}
      {!!groups.length && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th>{t("CI_MATCH_DOCUMENT")}</th>
                <th>{t("CI_ISSUER_DOMAIN")}</th>
                <th>{t("CI_REVIEW_ASSOCIATIONS")}</th>
                <th>{t("CI_REVIEW_STATUS")}</th>
                <th>{t("CI_REVIEW_ACTIONS")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(({ id, matches }) => {
                const doc = documents.find((d) => d.id === id)
                const sent = submissions.data?.pages
                  .flat()
                  .find(
                    (s) =>
                      s.candidateId === id &&
                      s.sha256 === matches[0].sha256 &&
                      s.issuerDomain === request.scope.issuerDomain &&
                      associationKey(s.matches, request.scope.issuerDomain) ===
                        associationKey(matches, request.scope.issuerDomain),
                  )
                return (
                  <tr key={id} className="border-t align-top">
                    <td className="py-3">
                      {doc?.filename ??
                        submissions.data?.pages
                          .flat()
                          .find((s) => s.candidateId === id)?.filename ??
                        id}
                      {doc && (
                        <SavedOriginalActions id={id} filename={doc.filename} />
                      )}
                    </td>
                    <td className="py-3">{request.scope.issuerDomain}</td>
                    <td className="py-3">
                      <ReviewAssociations matches={matches} />
                    </td>
                    <td className="py-3">
                      {sent
                        ? t(reviewStatusKeys[sent.status])
                        : t("CI_REVIEW_DRAFT")}
                    </td>
                    <td className="space-y-2 py-3">
                      {canEdit && (
                        <>
                          <Button
                            disabled={
                              busy ||
                              !!sent ||
                              !submissions.isSuccess ||
                              submissions.hasNextPage
                            }
                            onClick={() => send.mutate(id)}
                          >
                            {t("CI_REVIEW_SEND")}
                          </Button>
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              save.mutate(
                                request.matches.filter(
                                  (m) => m.candidateId !== id,
                                ),
                              )
                            }
                          >
                            {t("CI_MATCH_REMOVE")}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {submissions.hasNextPage && (
        <Button
          disabled={submissions.isFetchingNextPage}
          onClick={() => void submissions.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
      {canEdit && (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (!valid || !selected || busy) return
            const matches: EvidenceMatch[] = parts.map((partNumber) => ({
              candidateId: selected.id,
              sha256: selected.sha256,
              issuerDomain: request.scope.issuerDomain,
              partNumber,
              framework: framework as PilotFramework,
              decision: "supported",
              sourceUrl: selected.sourceUrl!,
              quote: "",
              scopeBasis: "exact_part",
              scopeReviewed: true,
              exclusionsReviewed: false,
            }))
            save.mutate([
              ...request.matches.filter(
                (m) =>
                  !(
                    m.candidateId === selected.id &&
                    m.framework === framework &&
                    parts.includes(m.partNumber)
                  ),
              ),
              ...matches,
            ])
          }}
        >
          <label className="block">
            {t("CI_MATCH_DOCUMENT")}
            <select
              className={selectClass}
              value={document}
              onChange={(e) => setDocument(e.target.value)}
            >
              <option value="">—</option>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.filename}
                </option>
              ))}
            </select>
          </label>
          {docs.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              disabled={docs.isFetchingNextPage}
              onClick={() => void docs.fetchNextPage()}
            >
              {t("CI_MORE")}
            </Button>
          )}
          {docs.isError && (
            <p role="alert">
              {t("CI_ERROR")}{" "}
              <Button type="button" onClick={() => void docs.refetch()}>
                {t("CI_RETRY")}
              </Button>
            </p>
          )}
          <label className="block">
            {t("CI_MATCH_FRAMEWORK")}
            <select
              className={selectClass}
              value={framework}
              onChange={(e) => {
                setFramework(e.target.value)
                setParts([])
              }}
            >
              <option value="">—</option>
              {[
                ...new Set(
                  request.scope.requirements.flatMap((r) => r.frameworks),
                ),
              ].map((f) => (
                <option key={f} value={f}>
                  {f.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <fieldset>
            <legend>{t("CI_MATCH_PART")}</legend>
            <div className="space-y-2 max-h-64 overflow-auto">
              {request.scope.requirements
                .filter((r) =>
                  r.frameworks.includes(framework as PilotFramework),
                )
                .map((r) => (
                  <label key={r.partNumber} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={parts.includes(r.partNumber)}
                      onChange={(e) =>
                        setParts((current) =>
                          e.target.checked
                            ? [...current, r.partNumber]
                            : current.filter((p) => p !== r.partNumber),
                        )
                      }
                    />
                    {r.partNumber}
                  </label>
                ))}
            </div>
          </fieldset>
          <Button type="submit" disabled={!valid || busy}>
            {t("CI_MATCH_SAVE")}
          </Button>
        </form>
      )}
      {save.isError && <p role="alert">{t("CI_MATCH_ERROR")}</p>}
      {send.isError && <p role="alert">{t("CI_REVIEW_SEND_ERROR")}</p>}
    </div>
  )
}
